import type { ElementType, ReactNode } from 'react';
import { useId, useState } from 'react';

import { Button } from '../button/Button';

export type NotificationVariant = 'default' | 'success' | 'info' | 'warning' | 'error';

interface NotificationActionBase {
  label: string;
  onClick?: () => void;
}

interface NotificationLinkAction extends NotificationActionBase {
  to: string;
  href?: never;
  state?: unknown;
}

interface NotificationHrefAction extends NotificationActionBase {
  href: string;
  to?: never;
  target?: '_blank';
}

interface NotificationButtonAction extends NotificationActionBase {
  href?: never;
  to?: never;
  onClick: () => void;
}

export type NotificationAction = NotificationLinkAction | NotificationHrefAction | NotificationButtonAction;

export interface NotificationProps {
  children: ReactNode;
  variant?: NotificationVariant;
  title?: ReactNode;
  titleAs?: ElementType;
  dismissible?: boolean;
  dismissalKey?: string;
  onDismiss?: () => void;
  action?: NotificationAction;
  className?: string;
  ariaLabel?: string;
  dismissLabel?: string;
}

export function Notification({
  children,
  variant = 'default',
  title,
  titleAs: Title = 'h2',
  dismissible = false,
  dismissalKey,
  onDismiss,
  action,
  className = '',
  ariaLabel = 'Notification',
  dismissLabel = 'Dismiss notification',
}: NotificationProps) {
  const titleId = useId();
  // Storage can throw under enterprise policy or locked-down Safari; never take the page with it.
  const [isDismissed, setIsDismissed] = useState(() => {
    if (!dismissalKey) return false;

    try {
      return localStorage.getItem(dismissalKey) === 'true';
    } catch {
      return false;
    }
  });

  const dismiss = () => {
    if (dismissalKey) {
      try {
        localStorage.setItem(dismissalKey, 'true');
      } catch {
        // Storage unavailable; the dismissal just won't survive a reload.
      }
    }

    setIsDismissed(true);
    onDismiss?.();
  };

  if (isDismissed) return null;

  const classes = ['notification', `notification--${variant}`, className].filter(Boolean).join(' ');

  return (
    <section
      className={classes}
      aria-label={title ? undefined : ariaLabel}
      aria-labelledby={title ? titleId : undefined}
    >
      <div className="notification__content">
        {title && (
          <Title id={titleId} className="notification__title">
            {title}
          </Title>
        )}
        <div className="notification__body">{children}</div>
      </div>

      {action && <NotificationActionButton action={action} />}

      {dismissible && (
        <button type="button" className="notification__close" onClick={dismiss} aria-label={dismissLabel}>
          ×
        </button>
      )}
    </section>
  );
}

function NotificationActionButton({ action }: { action: NotificationAction }) {
  if ('to' in action && action.to) {
    return (
      <Button className="notification__action" to={action.to} state={action.state} onClick={action.onClick}>
        {action.label}
      </Button>
    );
  }

  if ('href' in action && action.href) {
    return (
      <Button className="notification__action" href={action.href} target={action.target} onClick={action.onClick}>
        {action.label}
      </Button>
    );
  }

  return (
    <Button className="notification__action" onClick={action.onClick}>
      {action.label}
    </Button>
  );
}
